import React, { useState } from 'react'
import { formatCZK, ProgressBar } from './helpers/tableHelpers'
import { usePropertyMetadata } from '../../../contexts/PropertyMetadataContext'

const DetailedRow = ({ data, city, district }) => {
  const { propertyConditions, conditionFilterMap } = usePropertyMetadata()
  const conditionOrder = propertyConditions.map(({ value }) => value)
  const [activeTab, setActiveTab] = useState('overview')
  const [layoutSort, setLayoutSort] = useState('count-desc')
  const [conditionSort, setConditionSort] = useState({ field: 'totalCount', direction: 'desc' })
  const [conditionLayoutSort, setConditionLayoutSort] = useState({ field: 'totalCount', direction: 'desc' })

  const tabs = [
    { id: 'overview', label: 'Přehled' },
    { id: 'layoutAnalysis', label: 'Analýza dispozic' },
    { id: 'conditionAnalysis', label: 'Analýza stavu' },
    { id: 'conditionLayoutAnalysis', label: 'Analýza stav/dispozice' },
    { id: 'conditions', label: 'Stav nemovitostí' },
    { id: 'layouts', label: 'Dispozice' },
    { id: 'buildingTypes', label: 'Typ stavby' },
    { id: 'timeAnalysis', label: 'Analýza času' },
  ]

  // Sorting function for analysis tables
  const sortData = (data, sortConfig) => {
    if (!data) return []

    return Object.entries(data).sort(([keyA, a], [keyB, b]) => {
      let aValue, bValue

      // Handle string sorting for layout and condition columns
      if (sortConfig.field === 'layout' || sortConfig.field === 'condition') {
        aValue = keyA
        bValue = keyB
      } else {
        aValue = a[sortConfig.field]
        bValue = b[sortConfig.field]

        // Handle nested properties
        if (sortConfig.field === 'price') {
          aValue = a.price?.median
          bValue = b.price?.median
        } else if (sortConfig.field === 'pricePerM2') {
          aValue = a.pricePerM2?.median
          bValue = b.pricePerM2?.median
        } else if (sortConfig.field === 'timeOnMarket') {
          aValue = a.timeOnMarket?.avg
          bValue = b.timeOnMarket?.avg
        }
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = 0
      if (bValue === null || bValue === undefined) bValue = 0

      if (sortConfig.direction === 'asc') {
        return typeof aValue === 'string' ? aValue.localeCompare(bValue, 'cs') : aValue - bValue
      } else {
        return typeof aValue === 'string' ? bValue.localeCompare(aValue, 'cs') : bValue - aValue
      }
    })
  }

  // Handle sort click
  const handleSort = (field, currentSort, setSort) => {
    setSort({
      field,
      direction: currentSort.field === field && currentSort.direction === 'desc' ? 'asc' : 'desc',
    })
  }

  // Sortable table header component
  const SortableHeader = ({ field, currentSort, setSort, children }) => (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => handleSort(field, currentSort, setSort)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <span className="text-gray-400">{currentSort.field === field ? (currentSort.direction === 'desc' ? '▼' : '▲') : '↕'}</span>
      </div>
    </th>
  )

  return (
    <div className="bg-gray-50 p-4 border-t">
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-4 border-b overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[200px]">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Celkem inzerátů</div>
              <div className="text-2xl font-bold text-blue-600">{data.totalListings}</div>
              <div className="text-xs text-gray-500 mt-1">
                Aktivní: {data.activeListings} ({Math.round((data.activeListings / data.totalListings) * 100)}%)
              </div>
            </div>

            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Medián cena</div>
              <div className="text-2xl font-bold text-green-600">{formatCZK(data.price?.median)}</div>
              <div className="text-xs text-gray-500 mt-1">Průměr: {formatCZK(data.price?.average)}</div>
            </div>

            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Cena za m²</div>
              <div className="text-2xl font-bold text-purple-600">{data.pricePerM2?.median?.toLocaleString()} Kč/m²</div>
              <div className="text-xs text-gray-500 mt-1">Vážený: {data.pricePerM2?.weightedMedian?.toLocaleString()} Kč/m²</div>
            </div>

            <div className="bg-white p-3 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Průměrná velikost</div>
              <div className="text-2xl font-bold text-orange-600">{Math.round(data.size?.average)} m²</div>
              <div className="text-xs text-gray-500 mt-1">Medián: {data.size?.median} m²</div>
            </div>
          </div>
        )}

        {activeTab === 'conditions' && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800 mb-3">Rozdělení podle stavu nemovitostí</h4>
            {data.conditions &&
              Object.entries(data.conditions)
                .sort(([, a], [, b]) => b - a)
                .map(([condition, count]) => (
                  <div key={condition} className="flex items-center space-x-3">
                    <div className="w-32 text-sm text-gray-700">{condition}</div>
                    <div className="flex-1">
                      <ProgressBar value={count} max={data.totalListings} color="bg-green-500" />
                    </div>
                    <div className="w-16 text-sm font-medium text-right">
                      {count} ({Math.round((count / data.totalListings) * 100)}%)
                    </div>
                  </div>
                ))}
          </div>
        )}

        {activeTab === 'layouts' && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800 mb-3">Rozdělení podle dispozic</h4>
            {data.layouts &&
              Object.entries(data.layouts)
                .sort(([, a], [, b]) => b - a)
                .map(([layout, count]) => (
                  <div key={layout} className="flex items-center space-x-3">
                    <div className="w-16 text-sm text-gray-700 font-medium">{layout}</div>
                    <div className="flex-1">
                      <ProgressBar value={count} max={data.totalListings} color="bg-blue-500" />
                    </div>
                    <div className="w-16 text-sm font-medium text-right">
                      {count} ({Math.round((count / data.totalListings) * 100)}%)
                    </div>
                  </div>
                ))}
          </div>
        )}

        {activeTab === 'buildingTypes' && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800 mb-3">Rozdělení podle typu stavby</h4>
            {data.buildingTypes &&
              Object.entries(data.buildingTypes)
                .sort(([, a], [, b]) => b - a)
                .map(([type, count]) => (
                  <div key={type} className="flex items-center space-x-3">
                    <div className="w-24 text-sm text-gray-700">{type}</div>
                    <div className="flex-1">
                      <ProgressBar value={count} max={data.totalListings} color="bg-purple-500" />
                    </div>
                    <div className="w-16 text-sm font-medium text-right">
                      {count} ({Math.round((count / data.totalListings) * 100)}%)
                    </div>
                  </div>
                ))}
          </div>
        )}

        {activeTab === 'timeAnalysis' && (
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800 mb-3">Analýza doby na trhu</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="text-sm text-gray-600">Průměrná doba</div>
                <div className="text-xl font-bold text-blue-600">{Math.round(data.timeOnMarket?.avg)} dní</div>
                <div className="text-xs text-gray-500 mt-1">Medián: {Math.round(data.timeOnMarket?.median)} dní</div>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-sm">
                <div className="text-sm text-gray-600">Počet dokončených</div>
                <div className="text-xl font-bold text-green-600">{data.timeOnMarket?.count}</div>
                <div className="text-xs text-gray-500 mt-1">z {data.totalListings} celkem</div>
              </div>
            </div>

            {data.byCondition && (
              <div className="mt-4">
                <h5 className="font-medium text-gray-700 mb-2">Doba prodeje podle stavu</h5>
                <div className="space-y-2">
                  {Object.entries(data.byCondition)
                    .filter(([, data]) => data.timeOnMarket?.avg)
                    .sort(([, a], [, b]) => a.timeOnMarket.avg - b.timeOnMarket.avg)
                    .map(([condition, conditionData]) => (
                      <div key={condition} className="flex items-center justify-between bg-white p-2 rounded">
                        <span className="text-sm text-gray-700">{condition}</span>
                        <span className="text-sm font-medium">{Math.round(conditionData.timeOnMarket.avg)} dní</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'layoutAnalysis' && (
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800 mb-3">Detailní analýza podle dispozic</h4>

            {data.byLayout && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader field="layout" currentSort={layoutSort} setSort={setLayoutSort}>
                        Dispozice
                      </SortableHeader>
                      <SortableHeader field="totalCount" currentSort={layoutSort} setSort={setLayoutSort}>
                        Počet
                      </SortableHeader>
                      <SortableHeader field="pricePerM2" currentSort={layoutSort} setSort={setLayoutSort}>
                        Kč/m²
                      </SortableHeader>
                      <SortableHeader field="price" currentSort={layoutSort} setSort={setLayoutSort}>
                        Medián cena
                      </SortableHeader>
                      {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Průměr m²</th> */}
                      <SortableHeader field="timeOnMarket" currentSort={layoutSort} setSort={setLayoutSort}>
                        Doba prodeje
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortData(data.byLayout, layoutSort).map(([layout, layoutData]) => (
                      <tr key={layout} className="hover:bg-gray-50">
                        <td
                          className="px-4 py-3 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => {
                            // Convert "2+kk" to "2kk" format
                            const urlLayout = layout.replace('+', '')
                            const url = `/prodej/byt/${city}?layouts=${urlLayout}&districts=${district}`
                            window.open(url, '_blank')
                          }}
                        >
                          {layout}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{layoutData.totalCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{layoutData.pricePerM2?.median?.toLocaleString()} Kč/m²</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatCZK(layoutData.price?.median)}</td>
                        {/* <td className="px-4 py-3 text-sm text-gray-700">{Math.round(layoutData.size?.average)} m²</td> */}
                        <td className="px-4 py-3 text-sm text-gray-700">{layoutData.timeOnMarket?.avg ? Math.round(layoutData.timeOnMarket.avg) + ' dní' : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!data.byLayout && <div className="text-center py-8 text-gray-500">Data o dispozicích nejsou k dispozici pro tento okres.</div>}
          </div>
        )}

        {activeTab === 'conditionAnalysis' && (
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800 mb-3">Detailní analýza podle stavu nemovitostí</h4>

            {data.byCondition && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader field="condition" currentSort={conditionSort} setSort={setConditionSort}>
                        Stav
                      </SortableHeader>
                      <SortableHeader field="totalCount" currentSort={conditionSort} setSort={setConditionSort}>
                        Počet
                      </SortableHeader>
                      <SortableHeader field="pricePerM2" currentSort={conditionSort} setSort={setConditionSort}>
                        Kč/m²
                      </SortableHeader>
                      <SortableHeader field="price" currentSort={conditionSort} setSort={setConditionSort}>
                        Medián cena
                      </SortableHeader>
                      <SortableHeader field="timeOnMarket" currentSort={conditionSort} setSort={setConditionSort}>
                        Doba prodeje
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sortData(data.byCondition, conditionSort).map(([condition, conditionData]) => (
                      <tr key={condition} className="hover:bg-gray-50">
                        <td
                          className="px-4 py-3 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => {
                            // Map condition to the correct URL parameter based on Dashboard.js filtering
                            const urlCondition = conditionFilterMap[condition]
                            if (urlCondition) {
                              const url = `/prodej/byt/${city}?options=${urlCondition}&districts=${district}`
                              window.open(url, '_blank')
                            }
                          }}
                        >
                          {condition}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{conditionData.totalCount}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{conditionData.pricePerM2?.median?.toLocaleString()} Kč/m²</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatCZK(conditionData.price?.median)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {conditionData.timeOnMarket?.avg
                            ? `${Math.round(conditionData.timeOnMarket.avg)} ${
                                Math.round(conditionData.timeOnMarket.avg) === 1 ? 'den' : Math.round(conditionData.timeOnMarket.avg) < 5 ? 'dny' : 'dní'
                              }`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!data.byCondition && <div className="text-center py-8 text-gray-500">Data o stavu nemovitostí nejsou k dispozici pro tento okres.</div>}
          </div>
        )}

        {activeTab === 'conditionLayoutAnalysis' && (
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-800 mb-3">Detailní analýza podle stavu a dispozic</h4>

            {data.byConditionAndLayout && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableHeader field="condition" currentSort={conditionLayoutSort} setSort={setConditionLayoutSort}>
                        Stav / Dispozice
                      </SortableHeader>
                      <SortableHeader field="totalCount" currentSort={conditionLayoutSort} setSort={setConditionLayoutSort}>
                        Počet
                      </SortableHeader>
                      <SortableHeader field="pricePerM2" currentSort={conditionLayoutSort} setSort={setConditionLayoutSort}>
                        Kč/m²
                      </SortableHeader>
                      <SortableHeader field="price" currentSort={conditionLayoutSort} setSort={setConditionLayoutSort}>
                        Medián cena
                      </SortableHeader>
                      <SortableHeader field="timeOnMarket" currentSort={conditionLayoutSort} setSort={setConditionLayoutSort}>
                        Doba prodeje
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {conditionOrder.filter((condition) => data.byConditionAndLayout[condition]).map((condition) => {
                      const layouts = data.byConditionAndLayout[condition]
                      return (
                        <React.Fragment key={condition}>
                          {/* Condition header row */}
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-800">
                              {condition}
                            </td>
                          </tr>
                          {/* Layout rows */}
                          {sortData(layouts, conditionLayoutSort).map(([layout, layoutData]) => {
                            // Map condition to the correct URL parameter
                            const urlCondition = conditionFilterMap[condition]
                            // Convert layout format
                            const urlLayout = layout.replace('+', '')

                            return (
                              <tr
                                key={`${condition}-${layout}`}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => {
                                  if (urlCondition) {
                                    const url = `/prodej/byt/${city}?layouts=${urlLayout}&options=${urlCondition}&districts=${district}`
                                    window.open(url, '_blank')
                                  }
                                }}
                              >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 pl-8 hover:text-blue-600">{layout}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{layoutData.totalCount}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{layoutData.pricePerM2?.median?.toLocaleString()} Kč/m²</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{formatCZK(layoutData.price?.median)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {layoutData.timeOnMarket?.avg
                                    ? `${Math.round(layoutData.timeOnMarket.avg)} ${
                                        Math.round(layoutData.timeOnMarket.avg) === 1 ? 'den' : Math.round(layoutData.timeOnMarket.avg) < 5 ? 'dny' : 'dní'
                                      }`
                                    : '-'}
                                </td>
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      )
                    })}
                    {/* Handle any conditions that are not in the predefined order */}
                    {Object.entries(data.byConditionAndLayout)
                      .filter(([condition]) => !conditionOrder.includes(condition))
                      .map(([condition, layouts]) => (
                        <React.Fragment key={condition}>
                          <tr className="bg-gray-50">
                            <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-800">
                              {condition}
                            </td>
                          </tr>
                          {sortData(layouts, conditionLayoutSort).map(([layout, layoutData]) => {
                            const urlLayout = layout.replace('+', '')
                            return (
                              <tr
                                key={`${condition}-${layout}`}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => {
                                  const url = `/prodej/byt/${city}?layouts=${urlLayout}&districts=${district}`
                                  window.open(url, '_blank')
                                }}
                              >
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 pl-8 hover:text-blue-600">{layout}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{layoutData.totalCount}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{layoutData.pricePerM2?.median?.toLocaleString()} Kč/m²</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{formatCZK(layoutData.price?.median)}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">
                                  {layoutData.timeOnMarket?.avg
                                    ? `${Math.round(layoutData.timeOnMarket.avg)} ${
                                        Math.round(layoutData.timeOnMarket.avg) === 1 ? 'den' : Math.round(layoutData.timeOnMarket.avg) < 5 ? 'dny' : 'dní'
                                      }`
                                    : '-'}
                                </td>
                              </tr>
                            )
                          })}
                        </React.Fragment>
                      ))}
                  </tbody>
                </table>
              </div>
            )}

            {!data.byConditionAndLayout && <div className="text-center py-8 text-gray-500">Data o stavu a dispozicích nejsou k dispozici pro tento okres.</div>}
          </div>
        )}
      </div>
    </div>
  )
}

export default DetailedRow
